/**
 * ModerationIssues - the "Report an issue" queue, and one conversation
 * ---------------------------------------------------------------------
 * The moderator's side of ReportIssue.jsx. Two things in one component
 * because they are one job: read the list, open one, answer it, go back.
 *
 * THE ONLY ASYMMETRY WORTH KNOWING. The moderator sees exactly who wrote in -
 * name, role, email, and whether they are a minor - while the person sees
 * "Moderator" and nothing else. That is not an oversight in one direction or
 * the other:
 *
 *   The moderator needs the identity to act. "The upload button does nothing"
 *   is a different problem depending on whether it is a student on an old
 *   phone or the one poster whose account is in a strange state.
 *
 *   The person does not need it, and giving it to them costs something real -
 *   the moderator is also an ordinary member with a profile, and a student
 *   who complained should not spend the next week recognising them.
 *
 * The list is sorted by "waiting for a reply" rather than by time, because it
 * is a queue and not a history. A thread whose last message is the
 * moderator's own answer is the least interesting row on the page.
 */

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, LifeBuoy, Send } from 'lucide-react';

import { supportApi } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import { timeAgo } from '../utils/format';
import Loader from './Loader';
import EmptyState from './EmptyState';

export default function ModerationIssues() {
  const { t } = useI18n();
  const [threads, setThreads] = useState([]);
  const [openWith, setOpenWith] = useState(null); // the person whose thread is open
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const loadThreads = useCallback(async () => {
    try {
      const data = await supportApi.adminThreads();
      setThreads(data.threads || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const open = async (person) => {
    setError('');
    try {
      const data = await supportApi.adminThread(person._id);
      setOpenWith(data.person);
      setMessages(data.messages || []);
      // Reading it is what clears the count, so the queue is honest about
      // what is still waiting.
      await supportApi.adminMarkRead(person._id);
      loadThreads();
    } catch (err) {
      setError(err.message);
    }
  };

  const reply = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    setError('');
    try {
      const { message } = await supportApi.adminReply(openWith._id, body);
      setMessages((current) => [...current, message]);
      setDraft('');
      loadThreads();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Loader label={t('common.loading')} />;

  /* ---------------- ONE CONVERSATION ---------------- */
  if (openWith) {
    const minor = openWith.role === 'student' && openWith.age && openWith.age < 18;

    return (
      <div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setOpenWith(null);
            setMessages([]);
            setDraft('');
          }}
          style={{ marginBottom: 'var(--space-sm)' }}
        >
          <ArrowLeft size={14} aria-hidden="true" /> {t('moderation.issuesBackToList')}
        </button>

        <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
          <strong>
            {openWith.firstName} {openWith.lastName}
          </strong>
          <div className="text-sm text-muted">
            {openWith.email} · {openWith.role}
            {/* Age is shown only when it changes what you should do about it.
                A minor reporting that something is broken is a minor stuck
                with a broken site, which is a different urgency. */}
            {minor && ` · ${t('moderation.issuesMinor', { age: openWith.age })}`}
            {!openWith.isActive && ` · ${t('moderation.issuesClosedAccount')}`}
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>
            {error}
          </div>
        )}

        <div className="card">
          {messages.length === 0 ? (
            <p className="chat-empty">{t('moderation.issuesNothingYet')}</p>
          ) : (
            messages.map((message) => (
              <div
                key={message._id}
                className={`message-row${message.fromModerator ? ' is-mine' : ''}`}
              >
                <div className="message-bubble">
                  <p>{message.body}</p>
                  <span className="message-time">{timeAgo(message.createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Same shape as every other compose box on the site - see the note
            in ReportIssue.jsx. */}
        <form onSubmit={reply} className="chat-compose" style={{ borderTop: 'none' }}>
          <div className="chat-compose-row">
            <label className="sr-only" htmlFor="issue-reply">{t('moderation.issuesReply')}</label>
            <textarea
              id="issue-reply"
              rows={1}
              value={draft}
              maxLength={4000}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('moderation.issuesReplyPlaceholder')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  reply(event);
                }
              }}
            />
            <button
              type="submit"
              className="chat-send"
              disabled={sending || !draft.trim()}
              aria-label={t('moderation.issuesSend')}
            >
              <Send size={18} aria-hidden="true" />
            </button>
          </div>
        </form>

        <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
          {t('moderation.issuesAnonymousNote')}
        </p>
      </div>
    );
  }

  /* ---------------- THE QUEUE ---------------- */
  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>
          {error}
        </div>
      )}

      {threads.length === 0 ? (
        <EmptyState
          icon={LifeBuoy}
          title={t('moderation.issuesEmptyTitle')}
          message={t('moderation.issuesEmptyBody')}
        />
      ) : (
        <div className="thread-list">
          {threads.map((thread) => (
            <button
              type="button"
              key={thread.person._id}
              className={`thread${thread.unread > 0 ? ' is-unread' : ''}`}
              onClick={() => open(thread.person)}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none' }}
            >
              <div className="thread-body">
                <div className="thread-top">
                  <strong className="thread-name">
                    {thread.person.firstName} {thread.person.lastName}
                  </strong>
                  <span className="thread-when">
                    {thread.lastMessage ? timeAgo(thread.lastMessage.createdAt) : ''}
                  </span>
                </div>

                <div className="thread-about">
                  {thread.person.role}
                  {thread.person.role === 'student' &&
                    thread.person.age &&
                    thread.person.age < 18 &&
                    ` · ${t('moderation.issuesMinor', { age: thread.person.age })}`}
                </div>

                {thread.lastMessage && (
                  <div className="thread-preview">
                    {thread.lastMessage.fromModerator && (
                      <span className="text-muted">{t('moderation.issuesYou')} </span>
                    )}
                    {thread.lastMessage.body}
                  </div>
                )}
              </div>

              {thread.unread > 0 && (
                <span className="thread-unread" aria-label={`${thread.unread} unread`}>
                  {thread.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
